import type { ItemDefinition } from "@/types/item.types"

export const itemDefinitions: Record<string, ItemDefinition> = {
  // ============================================
  // SPECIAL ITEMS
  // ============================================

  "clone-activation-beacon": {
    id: "clone-activation-beacon",
    name: "Clone Activation Codex",
    description: "A codex bound to your stored clones. Each charge represents a life waiting to be activated.",
    cost: 0,
    rarity: "legendary",
    type: "special",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [],
    iconColor: "#3b82f6",
    image: "/images/item/trinkets/Icons_13_54.png",
  },

  // ============================================
  // CONSUMABLE ITEMS - Potions
  // ============================================

  "cebi-roots": {
    id: "cebi-roots",
    name: "Cebi Root Draught",
    description: "A small healing brew pressed from cebi roots. Restores 50 health over 5 seconds.",
    cost: 20,
    rarity: "common",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 5,
    effects: [{ type: "heal", value: 50, duration: 300 }],
    cooldown: 60,
    iconColor: "#7CFC00",
    unlockedBySkillLevel: 1,
    image: "/images/item/potions/1/Icons_12_1_15.png",
  },

  "minor-healing-potion": {
    id: "minor-healing-potion",
    name: "Minor Healing Potion",
    description: "A basic healing brew. Instantly restores 100 health.",
    cost: 75,
    rarity: "common",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 5,
    effects: [{ type: "heal", value: 100 }],
    cooldown: 180,
    iconColor: "#FF6B6B",
    image: "/images/item/potions/1/Icons_12_1_01.png",
  },

  "siegesmith-potion-healing": {
    id: "siegesmith-potion-healing",
    name: "Siegesmith's Potion of Healing",
    description: "A potent healing brew. Instantly restores 200 health.",
    cost: 200,
    rarity: "rare",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 3,
    effects: [{ type: "heal", value: 200 }],
    cooldown: 300,
    iconColor: "#FF6B6B",
    unlockedBySkillLevel: 2,
    image: "/images/item/potions/1/Icons_12_1_02.png",
  },

  "greater-hisan-salve": {
    id: "greater-hisan-salve",
    name: "Greater Hisan Salve",
    description: "Ancient dwarven medicine. Restores 500 health and 100 mana.",
    cost: 1000,
    rarity: "epic",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 2,
    effects: [
      { type: "heal", value: 500 },
      { type: "mana", value: 100 },
    ],
    cooldown: 600,
    iconColor: "#9932CC",
    unlockedBySkillLevel: 3,
    image: "/images/item/potions/3/Icons_12_3_01.png",
  },

  "mana-potion": {
    id: "mana-potion",
    name: "Mana Potion",
    description: "Restores 75 mana instantly.",
    cost: 100,
    rarity: "common",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 5,
    effects: [{ type: "mana", value: 75 }],
    cooldown: 180,
    iconColor: "#4169E1",
    image: "/images/item/potions/2/Icons_12_2_01.png",
  },

  "greater-mana-potion": {
    id: "greater-mana-potion",
    name: "Greater Mana Potion",
    description: "Restores 200 mana instantly.",
    cost: 300,
    rarity: "rare",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 3,
    effects: [{ type: "mana", value: 200 }],
    cooldown: 300,
    iconColor: "#4169E1",
    image: "/images/item/potions/2/Icons_12_2_02.png",
  },

  "siegesmith-potion-greater-healing": {
    id: "siegesmith-potion-greater-healing",
    name: "Siegesmith's Potion of Greater Healing",
    description: "Master-crafted elixir. Restores 600 health and 200 mana instantly.",
    cost: 1000,
    rarity: "epic",
    type: "consumable",
    category: "potion",
    stackable: true,
    maxStack: 3,
    effects: [
      { type: "heal", value: 600 },
      { type: "mana", value: 200 },
    ],
    cooldown: 600,
    iconColor: "#FF1493",
    unlockedBySkillLevel: 10,
    image: "/images/item/potions/4/Icons_12_4_01.png",
  },

  // ============================================
  // CONSUMABLE ITEMS - Scrolls & Utility
  // ============================================

  "scroll-of-teleportation": {
    id: "scroll-of-teleportation",
    name: "Scroll of Teleportation",
    description: "Instantly teleport to any allied tower or the spawn point.",
    cost: 500,
    rarity: "rare",
    type: "consumable",
    category: "trinket",
    stackable: true,
    maxStack: 3,
    effects: [{ type: "teleport", value: 0 }],
    cooldown: 1800,
    iconColor: "#00CED1",
    unlockedBySkillLevel: 4,
    image: "/images/item/trinkets/Icons_13_65.png",
  },

  "lure-pouch": {
    id: "lure-pouch",
    name: "Lure Bestiary",
    description: "Reads out a call that draws all enemies in range toward the target location for 8 seconds.",
    cost: 2500,
    rarity: "epic",
    type: "consumable",
    category: "trinket",
    stackable: true,
    maxStack: 2,
    effects: [{ type: "lure", value: 400, duration: 480 }],
    cooldown: 1200,
    iconColor: "#DAA520",
    unlockedBySkillLevel: 5,
    image: "/images/item/trinkets/Icons_13_78.png",
  },

  "orb-of-absorption": {
    id: "orb-of-absorption",
    name: "Tome of Absorption",
    description: "Absorbs the next 300 damage taken. Lasts until depleted or 30 seconds.",
    cost: 1500,
    rarity: "epic",
    type: "consumable",
    category: "trinket",
    stackable: true,
    maxStack: 2,
    effects: [{ type: "absorb", value: 300, duration: 1800 }],
    cooldown: 900,
    iconColor: "#4169E1",
    unlockedBySkillLevel: 6,
    image: "/images/item/trinkets/Icons_13_54.png",
  },

  // ============================================
  // EQUIPMENT ITEMS - Base (Common/Rare/Epic)
  // ============================================

  "leather-vest": {
    id: "leather-vest",
    name: "Leather Cap",
    description: "Basic head protection. +5 armor.",
    cost: 150,
    rarity: "common",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "armor", value: 5 }],
    iconColor: "#8B4513",
    image: "/images/item/armor/Icons_6_01.png",
  },

  "chainmail": {
    id: "chainmail",
    name: "Barred Helm",
    description: "A sturdy barred helm. +10 armor.",
    cost: 400,
    rarity: "rare",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "armor", value: 10 }],
    iconColor: "#708090",
    image: "/images/item/armor/Icons_6_10.png",
  },

  "steel-plate": {
    id: "steel-plate",
    name: "Steel Plate",
    description: "Heavy armor. +15 armor, +100 max health.",
    cost: 800,
    rarity: "epic",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 15 },
      { type: "max-health", value: 100 },
    ],
    iconColor: "#C0C0C0",
    image: "/images/item/armor/Icons_6_20.png",
  },

  "short-sword": {
    id: "short-sword",
    name: "Oaken Cudgel",
    description: "A simple club. +15 damage.",
    cost: 200,
    rarity: "common",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "damage-boost", value: 15 }],
    iconColor: "#C0C0C0",
    image: "/images/item/weapons/Icon_4_01.png",
  },

  "longsword": {
    id: "longsword",
    name: "Short Sword",
    description: "A reliable blade. +30 damage.",
    cost: 500,
    rarity: "rare",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "damage-boost", value: 30 }],
    iconColor: "#C0C0C0",
    image: "/images/item/weapons/Icon_4_05.png",
  },

  "battle-axe": {
    id: "battle-axe",
    name: "Battle Axe",
    description: "A heavy weapon. +50 damage.",
    cost: 900,
    rarity: "epic",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "damage-boost", value: 50 }],
    iconColor: "#8B4513",
    image: "/images/item/weapons/Icon_4_15.png",
  },

  "lucky-coin": {
    id: "lucky-coin",
    name: "Ledger of Fortune",
    description: "Increases gold from kills by 5%.",
    cost: 300,
    rarity: "rare",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "gold-bonus", value: 0.05 }],
    iconColor: "#FFD700",
    image: "/images/item/trinkets/Icons_13_64.png",
  },

  "ruby-ring": {
    id: "ruby-ring",
    name: "Ruby Grimoire",
    description: "Increases skill damage by 5%.",
    cost: 400,
    rarity: "rare",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "spell-power", value: 0.05 }],
    iconColor: "#DC143C",
    image: "/images/item/trinkets/Icons_13_55.png",
  },

  "swift-boots": {
    id: "swift-boots",
    name: "Helm of Haste",
    description: "Increases movement speed by 15%.",
    cost: 350,
    rarity: "rare",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "speed-boost", value: 0.15 }],
    iconColor: "#00CED1",
    image: "/images/item/armor/Icons_6_90.png",
  },

  // ============================================
  // LEGENDARY ITEMS - Base items with bonus stats
  // ============================================

  "ivory-mail": {
    id: "ivory-mail",
    name: "Ivory Hide Cap",
    description: "Legendary headgear cured from ancient beast hide. +10 armor, +200 max health.",
    cost: 15000,
    rarity: "legendary",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 10 },
      { type: "max-health", value: 200 },
    ],
    bonusEffects: [
      { type: "hp-regen", value: 5 },
    ],
    iconColor: "#FFFFF0",
    unlockedBySkillLevel: 7,
    image: "/images/item/armor/Icons_6_42.png",
  },

  "sunstone": {
    id: "sunstone",
    name: "Sunlit Codex",
    description: "A codex radiant with solar energy. +12% skill damage.",
    cost: 7500,
    rarity: "legendary",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [{ type: "spell-power", value: 0.12 }],
    bonusEffects: [
      { type: "mana-regen", value: 3 },
    ],
    iconColor: "#FFD700",
    unlockedBySkillLevel: 8,
    image: "/images/item/trinkets/Icons_13_63.png",
  },

  "dwarven-pride": {
    id: "dwarven-pride",
    name: "Dwarven Rapier",
    description: "The pinnacle of dwarven bladesmithing. +15 armor, +50 damage, +300 max health.",
    cost: 25000,
    rarity: "legendary",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 15 },
      { type: "damage-boost", value: 50 },
      { type: "max-health", value: 300 },
    ],
    bonusEffects: [
      { type: "damage-reflect", value: 0.05 },
    ],
    iconColor: "#B8860B",
    unlockedBySkillLevel: 9,
    image: "/images/item/weapons/Icon_4_87.png",
  },

  "bloodstone": {
    id: "bloodstone",
    name: "Bloodbound Grimoire",
    description: "Dark grimoire. 15% lifesteal on all attacks. Lose 5 health per second.",
    cost: 30000,
    rarity: "legendary",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "lifesteal", value: 0.15 },
      { type: "heal", value: -5, duration: 60 },
    ],
    bonusEffects: [
      { type: "damage-boost", value: 25 },
    ],
    iconColor: "#8B0000",
    unlockedBySkillLevel: 10,
    image: "/images/item/trinkets/Icons_13_56.png",
  },

  // ============================================
  // UNIQUE ITEMS - Weapons (Boss & Pelf drops only)
  // ============================================

  "soulreaver": {
    id: "soulreaver",
    name: "Soulreaver Horn",
    description: "A cursed horn that hungers for souls. +80 damage, +10% lifesteal.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 80 },
      { type: "lifesteal", value: 0.10 },
    ],
    iconColor: "#4B0082",
    image: "/images/item/weapons/Icon_4_50.png",
  },

  "doomhammer": {
    id: "doomhammer",
    name: "Doomglaive",
    description: "An ancient glaive of devastating power. +100 damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 100 },
    ],
    iconColor: "#2F4F4F",
    image: "/images/item/weapons/Icon_4_88.png",
  },

  "frostbrand": {
    id: "frostbrand",
    name: "Frostbound Stones",
    description: "Hurled stones of eternal winter. +60 damage, slows enemies.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 60 },
    ],
    iconColor: "#00BFFF",
    image: "/images/item/weapons/Icon_4_55.png",
  },

  "blazefury": {
    id: "blazefury",
    name: "Blazefury Talons",
    description: "Talons forged in dragon fire. +70 damage, +8% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 70 },
      { type: "spell-power", value: 0.08 },
    ],
    iconColor: "#FF4500",
    image: "/images/item/weapons/Icon_4_60.png",
  },

  "shadowstrike": {
    id: "shadowstrike",
    name: "Shadowstrike Lance",
    description: "A lance that strikes from the darkness. +50 damage, +25% crit chance.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 50 },
      { type: "crit-chance", value: 0.25 },
    ],
    iconColor: "#483D8B",
    image: "/images/item/weapons/Icon_4_30.png",
  },

  "voidcleaver": {
    id: "voidcleaver",
    name: "Voidtusk",
    description: "A tusk that cuts through reality itself. +90 damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 90 },
    ],
    iconColor: "#800080",
    image: "/images/item/weapons/Icon_4_65.png",
  },

  "thundercall": {
    id: "thundercall",
    name: "Thundercall Horn",
    description: "A war horn that channels the fury of storms. +65 damage, +10% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 65 },
      { type: "spell-power", value: 0.10 },
    ],
    iconColor: "#FFD700",
    image: "/images/item/weapons/Icon_4_70.png",
  },

  "dragonslayer": {
    id: "dragonslayer",
    name: "Dragonfire Chakram",
    description: "A ringed blade forged to fell the mightiest beasts. +120 damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 120 },
    ],
    iconColor: "#B22222",
    image: "/images/item/weapons/Icon_4_75.png",
  },

  "starfall": {
    id: "starfall",
    name: "Starfall Crescent",
    description: "A crescent blade blessed by the cosmos. +55 damage, +15% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 55 },
      { type: "spell-power", value: 0.15 },
    ],
    iconColor: "#E6E6FA",
    image: "/images/item/weapons/Icon_4_80.png",
  },

  "nightwhisper": {
    id: "nightwhisper",
    name: "Nightwhisper Wand",
    description: "Silent but deadly. +45 damage, +30% attack speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 45 },
      { type: "attack-speed", value: 0.30 },
    ],
    iconColor: "#191970",
    image: "/images/item/weapons/Icon_4_35.png",
  },

  "bonecrusher": {
    id: "bonecrusher",
    name: "Bone Pike",
    description: "A brutal pike of splintered bone. +75 damage, +200 max HP.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 75 },
      { type: "max-health", value: 200 },
    ],
    iconColor: "#808080",
    image: "/images/item/weapons/Icon_4_90.png",
  },

  "serpents-fang": {
    id: "serpents-fang",
    name: "Serpent's Fang Spear",
    description: "A spear coated in deadly venom. +40 damage, +15% lifesteal.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 40 },
      { type: "lifesteal", value: 0.15 },
    ],
    iconColor: "#228B22",
    image: "/images/item/weapons/Icon_4_40.png",
  },

  "phoenix-blade": {
    id: "phoenix-blade",
    name: "Phoenix Talon",
    description: "A talon that burns with immortal fire. +60 damage, +10 health regeneration.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 60 },
      { type: "hp-regen", value: 10 },
    ],
    iconColor: "#FF6347",
    image: "/images/item/weapons/Icon_4_45.png",
  },

  "worldender": {
    id: "worldender",
    name: "Worldender Spear",
    description: "Power beyond measure, at a cost. +150 damage, -200 max HP.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 150 },
      { type: "max-health", value: -200 },
    ],
    iconColor: "#8B0000",
    image: "/images/item/weapons/Icon_4_95.png",
  },

  "divine-wrath": {
    id: "divine-wrath",
    name: "Divine Wrath",
    description: "Holy vengeance incarnate. +100 damage, +10 mana regen.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "weapon",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 100 },
      { type: "mana-regen", value: 10 },
    ],
    iconColor: "#FFFACD",
    image: "/images/item/weapons/Icon_4_100.png",
  },

  // ============================================
  // UNIQUE ITEMS - Armor (Boss & Pelf drops only)
  // ============================================

  "aegis-of-the-ancients": {
    id: "aegis-of-the-ancients",
    name: "Helm of the Ancients",
    description: "Worn by primordial guardians. +25 armor, +400 HP.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 25 },
      { type: "max-health", value: 400 },
    ],
    iconColor: "#DAA520",
    image: "/images/item/armor/Icons_6_50.png",
  },

  "dragonscale-vest": {
    id: "dragonscale-vest",
    name: "Dragonscale Vest",
    description: "Scales of an ancient dragon. +20 armor, +8% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 20 },
      { type: "spell-power", value: 0.08 },
    ],
    iconColor: "#228B22",
    image: "/images/item/armor/Icons_6_55.png",
  },

  "shadow-cloak": {
    id: "shadow-cloak",
    name: "Shadowplate Cuirass",
    description: "Plate forged from darkness itself. +10 armor, +30% speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 10 },
      { type: "speed-boost", value: 0.30 },
    ],
    iconColor: "#2F2F2F",
    image: "/images/item/armor/Icons_6_60.png",
  },

  "titans-bulwark": {
    id: "titans-bulwark",
    name: "Titan's Gauntlet",
    description: "Gauntlet of the giants. +35 armor, 10% damage reflect.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 35 },
      { type: "damage-reflect", value: 0.10 },
    ],
    iconColor: "#708090",
    image: "/images/item/armor/Icons_6_65.png",
  },

  "ethereal-shroud": {
    id: "ethereal-shroud",
    name: "Ethereal Bracer",
    description: "Phase through the veil. +15 armor, +25% speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 15 },
      { type: "speed-boost", value: 0.25 },
    ],
    iconColor: "#E6E6FA",
    image: "/images/item/armor/Icons_6_70.png",
  },

  "ironbark-plate": {
    id: "ironbark-plate",
    name: "Ironbark Boots",
    description: "Grown from the World Tree. +30 armor, +15 HP regen.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 30 },
      { type: "hp-regen", value: 15 },
    ],
    iconColor: "#8B4513",
    image: "/images/item/armor/Icons_6_75.png",
  },

  "frostguard": {
    id: "frostguard",
    name: "Frostguard Boots",
    description: "Forged in eternal winter. +18 armor, +20% attack speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 18 },
      { type: "attack-speed", value: 0.20 },
    ],
    iconColor: "#00BFFF",
    image: "/images/item/armor/Icons_6_80.png",
  },

  "sunforged-mail": {
    id: "sunforged-mail",
    name: "Sunforged Helm",
    description: "Blessed by solar fire. +22 armor, +10% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 22 },
      { type: "spell-power", value: 0.10 },
    ],
    iconColor: "#FFD700",
    image: "/images/item/armor/Icons_6_85.png",
  },

  "voidwalkers-mantle": {
    id: "voidwalkers-mantle",
    name: "Voidwalker's Helm",
    description: "Step between dimensions. +12 armor, +35% speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 12 },
      { type: "speed-boost", value: 0.35 },
    ],
    iconColor: "#4B0082",
    image: "/images/item/armor/Icons_6_45.png",
  },

  "warlords-regalia": {
    id: "warlords-regalia",
    name: "Warlord's Gauntlet",
    description: "Gauntlet of conquerors. +28 armor, +40 damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 28 },
      { type: "damage-boost", value: 40 },
    ],
    iconColor: "#8B0000",
    image: "/images/item/armor/Icons_6_30.png",
  },

  "phantom-vestments": {
    id: "phantom-vestments",
    name: "Phantom Treads",
    description: "Light as a whisper. +8 armor, +40% speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 8 },
      { type: "speed-boost", value: 0.40 },
    ],
    iconColor: "#778899",
    image: "/images/item/armor/Icons_6_35.png",
  },

  "earthen-shield": {
    id: "earthen-shield",
    name: "Earthen Gauntlet",
    description: "Immovable as a mountain. +40 armor, -15% speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 40 },
      { type: "speed-boost", value: -0.15 },
    ],
    iconColor: "#8B4513",
    image: "/images/item/armor/Icons_6_25.png",
  },

  "celestial-robes": {
    id: "celestial-robes",
    name: "Celestial Greaves",
    description: "Blessed by the stars. +5 armor, +8 mana regen, +18% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 5 },
      { type: "spell-power", value: 0.18 },
      { type: "mana-regen", value: 8 },
    ],
    iconColor: "#E6E6FA",
    image: "/images/item/armor/Icons_6_40.png",
  },

  "blood-knights-armor": {
    id: "blood-knights-armor",
    name: "Blood Knight's Armor",
    description: "Sustained by battle. +20 armor, 12% lifesteal.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 20 },
      { type: "lifesteal", value: 0.12 },
    ],
    iconColor: "#8B0000",
    image: "/images/item/armor/Icons_6_15.png",
  },

  "crown-of-thorns": {
    id: "crown-of-thorns",
    name: "Thornmail Hauberk",
    description: "Pain returned tenfold. +25 armor, 20% damage reflect.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "armor",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "armor", value: 25 },
      { type: "damage-reflect", value: 0.20 },
    ],
    iconColor: "#228B22",
    image: "/images/item/armor/Icons_6_95.png",
  },

  // ============================================
  // UNIQUE ITEMS - Trinkets (Boss & Pelf drops only)
  // ============================================

  "eye-of-the-storm": {
    id: "eye-of-the-storm",
    name: "Tome of the Storm",
    description: "Channel tempest power. +15% skill damage, +8 mana regen.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "spell-power", value: 0.15 },
      { type: "mana-regen", value: 8 },
    ],
    iconColor: "#87CEEB",
    image: "/images/item/trinkets/Icons_13_52.png",
  },

  "heart-of-the-mountain": {
    id: "heart-of-the-mountain",
    name: "Stonebound Tome",
    description: "Stone-cold endurance. +500 HP, +15 armor.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "max-health", value: 500 },
      { type: "armor", value: 15 },
    ],
    iconColor: "#8B4513",
    image: "/images/item/trinkets/Icons_13_57.png",
  },

  "talisman-of-speed": {
    id: "talisman-of-speed",
    name: "Treatise of Haste",
    description: "Swift as the wind. +50% move speed, +20% attack speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "speed-boost", value: 0.50 },
      { type: "attack-speed", value: 0.20 },
    ],
    iconColor: "#00CED1",
    image: "/images/item/trinkets/Icons_13_58.png",
  },

  "orb-of-omniscience": {
    id: "orb-of-omniscience",
    name: "Codex of Omniscience",
    description: "See all, know all. +25% crit chance, +10% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "crit-chance", value: 0.25 },
      { type: "spell-power", value: 0.10 },
    ],
    iconColor: "#9400D3",
    image: "/images/item/trinkets/Icons_13_59.png",
  },

  "pendant-of-fortune": {
    id: "pendant-of-fortune",
    name: "Almanac of Fortune",
    description: "Luck favors you. +50% gold from kills.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "gold-bonus", value: 0.50 },
    ],
    iconColor: "#FFD700",
    image: "/images/item/trinkets/Icons_13_60.png",
  },

  "ring-of-power": {
    id: "ring-of-power",
    name: "Tome of Power",
    description: "Raw magical might. +40 damage, +12% skill damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 40 },
      { type: "spell-power", value: 0.12 },
    ],
    iconColor: "#FF4500",
    image: "/images/item/trinkets/Icons_13_61.png",
  },

  "amulet-of-souls": {
    id: "amulet-of-souls",
    name: "Codex of Souls",
    description: "Harvest life force. +15% lifesteal, +200 HP.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "lifesteal", value: 0.15 },
      { type: "max-health", value: 200 },
    ],
    iconColor: "#4B0082",
    image: "/images/item/trinkets/Icons_13_62.png",
  },

  "crystal-of-eternity": {
    id: "crystal-of-eternity",
    name: "Codex of Eternity",
    description: "Time bends to your will. -30% cooldowns.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "cooldown-reduction", value: 0.30 },
    ],
    iconColor: "#00FFFF",
    image: "/images/item/trinkets/Icons_13_66.png",
  },

  "sigil-of-dominion": {
    id: "sigil-of-dominion",
    name: "Tome of Dominion",
    description: "Command with authority. +50 damage, +20% attack speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 50 },
      { type: "attack-speed", value: 0.20 },
    ],
    iconColor: "#8B0000",
    image: "/images/item/trinkets/Icons_13_67.png",
  },

  "badge-of-valor": {
    id: "badge-of-valor",
    name: "Chronicle of Valor",
    description: "Courage under fire. +30 damage, +15 armor.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 30 },
      { type: "armor", value: 15 },
    ],
    iconColor: "#FFD700",
    image: "/images/item/trinkets/Icons_13_68.png",
  },

  "relic-of-the-void": {
    id: "relic-of-the-void",
    name: "Void Codex",
    description: "Dark power unleashed. +18% skill damage, +25 damage.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "spell-power", value: 0.18 },
      { type: "damage-boost", value: 25 },
    ],
    iconColor: "#4B0082",
    image: "/images/item/trinkets/Icons_13_69.png",
  },

  "charm-of-resilience": {
    id: "charm-of-resilience",
    name: "Treatise of Resilience",
    description: "Endure any hardship. +300 HP, +20 armor.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "max-health", value: 300 },
      { type: "armor", value: 20 },
    ],
    iconColor: "#228B22",
    image: "/images/item/trinkets/Icons_13_70.png",
  },

  "token-of-greed": {
    id: "token-of-greed",
    name: "Ledger of Greed",
    description: "Wealth at any cost. +10% gold, -50% movement speed, -200 max HP.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "gold-bonus", value: 0.1 },
      { type: "speed-boost", value: -0.5 },
      { type: "max-health", value: -200 },
    ],
    iconColor: "#FFD700",
    image: "/images/item/trinkets/Icons_13_71.png",
  },

  "emblem-of-war": {
    id: "emblem-of-war",
    name: "War Codex",
    description: "Born for battle. +35 damage, +25% attack speed.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "damage-boost", value: 35 },
      { type: "attack-speed", value: 0.25 },
    ],
    iconColor: "#B22222",
    image: "/images/item/trinkets/Icons_13_72.png",
  },

  "phylactery": {
    id: "phylactery",
    name: "Phylactery Tome",
    description: "Cheat death itself. +10% lifesteal, +10 HP regen.",
    cost: 0,
    rarity: "unique",
    type: "equipment",
    category: "trinket",
    stackable: false,
    maxStack: 1,
    effects: [
      { type: "lifesteal", value: 0.10 },
      { type: "hp-regen", value: 10 },
    ],
    iconColor: "#2F4F4F",
    image: "/images/item/trinkets/Icons_13_73.png",
  },
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Module-level cached item arrays (computed once, reused)
let cachedAllItems: ItemDefinition[] | null = null
let cachedEquipmentItems: ItemDefinition[] | null = null
let cachedConsumableItems: ItemDefinition[] | null = null
let cachedUniqueItems: ItemDefinition[] | null = null

// Get all items (cached)
const getAllItems = (): ItemDefinition[] => {
  if (!cachedAllItems) {
    cachedAllItems = Object.values(itemDefinitions)
  }
  return cachedAllItems
}

// Get equipment items excluding unique rarity (cached)
const getEquipmentItems = (): ItemDefinition[] => {
  if (!cachedEquipmentItems) {
    cachedEquipmentItems = getAllItems().filter(
      (item) => item.type === "equipment" && item.rarity !== "unique"
    )
  }
  return cachedEquipmentItems
}

// Get consumable items (cached)
const getConsumableItems = (): ItemDefinition[] => {
  if (!cachedConsumableItems) {
    cachedConsumableItems = getAllItems().filter(
      (item) => item.type === "consumable"
    )
  }
  return cachedConsumableItems
}

// Helper to get all items unlocked at or below a certain skill level
export const getUnlockedItems = (skillLevel: number): ItemDefinition[] => {
  return getAllItems().filter(
    (item) => item.unlockedBySkillLevel !== undefined && item.unlockedBySkillLevel <= skillLevel,
  )
}

// Helper to get a random item from unlocked items
export const getRandomUnlockedItem = (skillLevel: number): ItemDefinition | null => {
  const unlockedItems = getUnlockedItems(skillLevel)
  if (unlockedItems.length === 0) return null
  return unlockedItems[Math.floor(Math.random() * unlockedItems.length)]
}

// Helper to check if an item is unlocked
export const isItemUnlocked = (itemDefId: string, skillLevel: number): boolean => {
  const item = itemDefinitions[itemDefId]
  if (!item) return false
  if (item.unlockedBySkillLevel === undefined) return true
  return item.unlockedBySkillLevel <= skillLevel
}

// Helper to get N random unique items (for starting inventory)
export const getRandomUniqueItems = (count: number): ItemDefinition[] => {
  const allItems = getAllItems()
  if (allItems.length <= count) return [...allItems]

  const shuffled = [...allItems].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

// Helper to get items for the phase terminal
export const getPhaseTerminalItems = (count: number): ItemDefinition[] => {
  const cloneBeacon = itemDefinitions["clone-activation-beacon"]
  if (!cloneBeacon) {
    return getRandomUniqueItems(count)
  }

  const otherItems = getAllItems().filter(
    item => item.id !== "clone-activation-beacon" && item.rarity !== "unique"
  )

  const shuffled = [...otherItems].sort(() => Math.random() - 0.5)
  const selectedOthers = shuffled.slice(0, count - 1)

  return [cloneBeacon, ...selectedOthers]
}

// Clone charge pseudo-item for shop display
export const cloneChargeItem: ItemDefinition = {
  id: "clone-charge",
  name: "Clone Charge",
  description: "Adds one charge to your Clone Activation Codex. Each charge is an extra life.",
  cost: 50,
  rarity: "legendary",
  type: "special",
  category: "trinket",
  stackable: false,
  maxStack: 1,
  effects: [],
  iconColor: "#3b82f6",
  image: itemDefinitions["clone-activation-beacon"]?.image || "/images/item/trinkets/Icons_13_54.png",
}

// Get random equipment (permanent) item - excludes unique items
export const getRandomEquipmentItem = (): ItemDefinition | null => {
  const items = getEquipmentItems()
  if (items.length === 0) return null
  return items[Math.floor(Math.random() * items.length)]
}

// Get random consumable item
export const getRandomConsumableItem = (): ItemDefinition | null => {
  const items = getConsumableItems()
  if (items.length === 0) return null
  return items[Math.floor(Math.random() * items.length)]
}

// Get shop window items (clone charge + equipment + consumable)
export const getShopWindowItems = (): ItemDefinition[] => {
  const equipment = getRandomEquipmentItem()
  const consumable = getRandomConsumableItem()

  return [
    cloneChargeItem,
    equipment || cloneChargeItem,
    consumable || cloneChargeItem,
  ]
}

// Get rerollable shop items (equipment + consumable only, no clone charge)
// Returns array of 2 items for positions 1 and 2 in the shop
export const getRerollableShopItems = (): [ItemDefinition | null, ItemDefinition | null] => {
  const equipment = getRandomEquipmentItem()
  const consumable = getRandomConsumableItem()
  return [equipment, consumable]
}

// ============================================
// UNIQUE ITEM HELPERS (for boss/pelf drops)
// ============================================

// Get all unique items (cached)
export const getUniqueItems = (): ItemDefinition[] => {
  if (!cachedUniqueItems) {
    cachedUniqueItems = getAllItems().filter(item => item.rarity === "unique")
  }
  return cachedUniqueItems
}

// Get unique items by category
export const getUniqueItemsByCategory = (category: "weapon" | "armor" | "trinket"): ItemDefinition[] => {
  return getUniqueItems().filter(item => item.category === category)
}

// Get random unique item (for pelf events and boss drops)
export const getRandomUniqueItem = (): ItemDefinition | null => {
  const uniqueItems = getUniqueItems()
  if (uniqueItems.length === 0) return null
  return uniqueItems[Math.floor(Math.random() * uniqueItems.length)]
}

// Get random unique item by difficulty tier (higher tier = better items)
export const getRandomUniqueItemByTier = (tier: number): ItemDefinition | null => {
  const uniqueItems = getUniqueItems()
  if (uniqueItems.length === 0) return null

  // Simple tier system: higher tier has chance for better items
  // Tier 1-3: any unique, Tier 4+: prefer weapons/armor over trinkets
  if (tier >= 4) {
    const weaponsAndArmor = uniqueItems.filter(
      item => item.category === "weapon" || item.category === "armor"
    )
    if (weaponsAndArmor.length > 0 && Math.random() > 0.3) {
      return weaponsAndArmor[Math.floor(Math.random() * weaponsAndArmor.length)]
    }
  }

  return uniqueItems[Math.floor(Math.random() * uniqueItems.length)]
}
